import { useState, useEffect } from 'react';
import { getAnalyticsOverview } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, RadialBarChart, RadialBar } from 'recharts';
import { Users, BookOpen, FileText, TrendingUp } from 'lucide-react';

const COLORS = ['#062B1A', '#D4AF37', 'rgba(6, 43, 26,0.65)', 'rgba(212, 175, 55,0.65)', '#000'];
const SCORE_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#062B1A'];

export default function AdminAnalytics() {
  const { loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (!authLoading) loadData(); }, [authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getAnalyticsOverview();
      setData(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1,2,3,4].map(i => <div key={i} className="h-72 bg-white rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!data) return <p className="text-center py-16 text-[#062B1A]/50">No analytics data</p>;

  const totals = data.totals || {};

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Analytics & Reports</h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">Deep insights into platform performance and student outcomes</p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Total Students</span>
              <Users className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{totals.students}</p>
          </CardContent>
        </Card>
        <Card className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm gold-border-left">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Total Courses</span>
              <BookOpen className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{totals.courses}</p>
          </CardContent>
        </Card>
        <Card className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Total Submissions</span>
              <FileText className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{totals.submissions}</p>
          </CardContent>
        </Card>
        <Card className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm gold-border-left">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Avg Enrollment</span>
              <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{totals.avg_enrollment}</p>
          </CardContent>
        </Card>
      </div>

      {/* Row 1: Enrollment + Category Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Enrollment by Course</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.enrollment_data} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 43, 26,0.08)" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#062B1A' }} />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: '#062B1A' }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(6, 43, 26,0.12)' }} />
                <Bar dataKey="enrolled" fill="#062B1A" radius={[0, 4, 4, 0]} name="Enrolled" />
                <Bar dataKey="lessons" fill="#D4AF37" radius={[0, 4, 4, 0]} name="Lessons" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Category Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.category_distribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {data.category_distribution.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Completion Rates + Score Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Average Progress by Course</CardTitle>
          </CardHeader>
          <CardContent>
            {data.completion_data.length === 0 ? (
              <p className="text-sm text-[#062B1A]/40 text-center py-12">No completion data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.completion_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 43, 26,0.08)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#062B1A' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#062B1A' }} domain={[0, 100]} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(6, 43, 26,0.12)' }} />
                  <Bar dataKey="avg_progress" fill="#D4AF37" radius={[4, 4, 0, 0]} name="Avg Progress %" />
                  <Bar dataKey="completion_rate" fill="#062B1A" radius={[4, 4, 0, 0]} name="Completion Rate %" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.score_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 43, 26,0.08)" />
                <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#062B1A' }} />
                <YAxis tick={{ fontSize: 11, fill: '#062B1A' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(6, 43, 26,0.12)' }} />
                <Bar dataKey="count" name="Students" radius={[4, 4, 0, 0]}>
                  {data.score_distribution.map((_, idx) => <Cell key={idx} fill={SCORE_COLORS[idx % SCORE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Submission breakdown + Course types */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Submission Types</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.submission_breakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" label>
                  {data.submission_breakdown.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Course Types</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.type_distribution} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" label>
                  {data.type_distribution.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Course Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.status_distribution} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" label>
                  {data.status_distribution.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
